import { useState } from 'react';

export interface CRUDModalState<T> {
  isOpen: boolean;
  mode: 'add' | 'edit' | null;
  editingItem: T | null;
  isDeleteModalOpen: boolean;
  deleteItemId: string | null;
}

export interface CRUDModalActions<T> {
  openAddModal: () => void;
  openEditModal: (item: T) => void;
  closeModal: () => void;
  openDeleteModal: (id: string) => void;
  closeDeleteModal: () => void;
}

export type UseCRUDModalReturn<T> = CRUDModalState<T> & CRUDModalActions<T>;

/**
 * Хук для управления состоянием модальных окон CRUD операций
 * @template T - тип редактируемого элемента
 * @returns объект с состоянием и действиями для модальных окон
 */
export function useCRUDModal<T extends { id: string }>(): UseCRUDModalReturn<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'add' | 'edit' | null>(null);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);

  const openAddModal = () => {
    setMode('add');
    setEditingItem(null);
    setIsOpen(true);
  };

  const openEditModal = (item: T) => {
    setMode('edit');
    setEditingItem(item);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setMode(null);
    setEditingItem(null);
  };

  const openDeleteModal = (id: string) => {
    setDeleteItemId(id);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteItemId(null);
  };

  return {
    // State
    isOpen,
    mode,
    editingItem,
    isDeleteModalOpen,
    deleteItemId,
    // Actions
    openAddModal,
    openEditModal,
    closeModal,
    openDeleteModal,
    closeDeleteModal,
  };
}
